import { Timestamp } from "@bufbuild/protobuf";

import {
  UpdateBillingFrequencyRequest,
  UpdateBillingFrequencyResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/contracts_pb";
import {
  UpdateContractTermRequest,
  UpdateContractTermResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import {
  SetContractEnterpriseStateRequest,
  SetContractEnterpriseStateResponse,
  SetContractMasterServiceAgreementStateRequest,
  SetContractMasterServiceAgreementStateResponse,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_contracts_pb";
import {
  GetOrgContractListRequest,
  GetOrgContractListResponse,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { supportServiceConnectClient } from "../index";
import { RequestMetadata } from "api/types";

type UpdateBillingFrequencyParams = {
  orgId: string;
  subscriptionId: string;
  toBillingFrequency: number;
  billingEndDate: Timestamp;
};

export const updateBillingFrequency = async (
  {
    orgId,
    subscriptionId,
    toBillingFrequency,
    billingEndDate,
  }: UpdateBillingFrequencyParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateBillingFrequencyResponse> => {
  const req = new UpdateBillingFrequencyRequest({
    organizationId: orgId,
    subscriptionId,
    toBillingFrequency: toBillingFrequency,
    billingEndDate,
    // no proration on frequency change
    shouldProrate: false,
  });
  return supportServiceConnectClient.updateBillingFrequency(req, { headers });
};

type UpdateContractTermParams = {
  orgId: string;
  subscriptionId: string;
  contractTermInMonths: number;
  contractEndDate: Timestamp;
};

export const updateContractTerm = async (
  {
    orgId,
    subscriptionId,
    contractTermInMonths,
    contractEndDate,
  }: UpdateContractTermParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateContractTermResponse> => {
  const req = new UpdateContractTermRequest({
    organizationId: orgId,
    subscriptionId,
    contractTermInMonths,
    contractEndDate,
    // no proration on contract term change
    shouldProrate: false,
  });

  return supportServiceConnectClient.updateContractTerm(req, { headers });
};

type GetOrgContractListParams = {
  orgId: string;
};

export const getOrgContractList = async (
  { orgId }: GetOrgContractListParams,
  headers: Record<RequestMetadata, string>
): Promise<GetOrgContractListResponse> => {
  const req = new GetOrgContractListRequest({
    organisationId: orgId,
  });

  return supportServiceConnectClient.getOrgContractList(req, { headers });
};

type SetContractEnterpriseStateParams = {
  contractId: string;
  hasEnterprise: boolean;
};

export const setContractEnterpriseState = async (
  { contractId, hasEnterprise }: SetContractEnterpriseStateParams,
  headers: Record<RequestMetadata, string>
): Promise<SetContractEnterpriseStateResponse> => {
  const req = new SetContractEnterpriseStateRequest({
    contractId: contractId,
    enterpriseState: hasEnterprise,
  });

  return supportServiceConnectClient.setContractEnterpriseState(req, {
    headers,
  });
};

type SetContractMSAStateParams = {
  contractId: string;
  hasMasterServiceAgreement: boolean;
};

export const setContractMSAState = async (
  { contractId, hasMasterServiceAgreement }: SetContractMSAStateParams,
  headers: Record<RequestMetadata, string>
): Promise<SetContractMasterServiceAgreementStateResponse> => {
  const req = new SetContractMasterServiceAgreementStateRequest({
    contractId: contractId,
    msaState: hasMasterServiceAgreement,
  });

  return supportServiceConnectClient.setContractMasterServiceAgreementState(
    req,
    { headers }
  );
};
